// snappingUtils.js
import { getRectangleCorners } from './geometryutils';

const SNAP_THRESHOLD = 10; // pixels in screen space
const GRID_SIZE = 50; // size of grid cells in world space
const COLLISION_TOLERANCE = 0.1; // Small tolerance for perfect alignment

// Improved snap point finding that considers element dimensions
export const findNearestSnapPoint = (position, elements, zoom, panOffset, excludeId = null, elementWidth = 0, elementHeight = 0) => {
    // Convert screen position to world position
    // const worldX = (position.x - panOffset.x) / zoom;
    // const worldY = (position.y - panOffset.y) / zoom;

    const worldX = position.x;
    const worldY = position.y;

    // Calculate element edges in world space
    const rightEdge = worldX + elementWidth;
    const bottomEdge = worldY + elementHeight;

    // Get snap candidates from grid
    const gridSnapX = Math.round(worldX / GRID_SIZE) * GRID_SIZE;
    const gridSnapY = Math.round(worldY / GRID_SIZE) * GRID_SIZE;

    // Initialize snap points with grid positions
    let snapPoints = {
        x: [gridSnapX],
        y: [gridSnapY]
    };

    // Get snap candidates from other elements
    elements.forEach(element => {
        if (element.id === excludeId) return;

        // For rotated elements, get corner positions
        if (element.rotation && element.rotation !== 0) {
            const corners = getRectangleCorners(element);
            corners.forEach(corner => {
                snapPoints.x.push(corner.x);
                snapPoints.y.push(corner.y);
                // Add offset snap points
                snapPoints.x.push(corner.x - elementWidth);
                snapPoints.y.push(corner.y - elementHeight);
            });
            
            // Add center point
            const centerX = (element.x1 + element.x2) / 2;
            const centerY = (element.y1 + element.y2) / 2;
            snapPoints.x.push(centerX);
            snapPoints.y.push(centerY);
            snapPoints.x.push(centerX - elementWidth / 2);
            snapPoints.y.push(centerY - elementHeight / 2);
        } else {
            // For non-rotated elements, use original logic
            // Add all possible snap points for x-axis
            snapPoints.x.push(element.x1); // Left edge
            snapPoints.x.push(element.x2); // Right edge
            snapPoints.x.push(element.x1 + (element.x2 - element.x1) / 2); // Center

            // Add offset snap points for right edge alignment
            snapPoints.x.push(element.x1 - elementWidth); // Align right to left
            snapPoints.x.push(element.x2 - elementWidth); // Align right to right

            // Add all possible snap points for y-axis
            snapPoints.y.push(element.y1); // Top edge
            snapPoints.y.push(element.y2); // Bottom edge
            snapPoints.y.push(element.y1 + (element.y2 - element.y1) / 2); // Center

            // Add offset snap points for bottom edge alignment
            snapPoints.y.push(element.y1 - elementHeight); // Align bottom to top
            snapPoints.y.push(element.y2 - elementHeight); // Align bottom to bottom
        }
    });

    // Find nearest snap point with improved precision
    const getNearestPoint = (value, points) => {
        let nearest = value;
        let minDist = Infinity;

        points.forEach(point => {
            const dist = Math.abs(value - point);
            if (dist < minDist && dist * zoom < SNAP_THRESHOLD) {
                minDist = dist;
                nearest = point;
            }
        });

        return nearest;
    };

    return {
        x: getNearestPoint(worldX, snapPoints.x),
        y: getNearestPoint(worldY, snapPoints.y)
    };
};

// Improved collision detection with tolerance
export const isCollidingWithTolerance = (rect1, rect2) => {
    // Add small tolerance to allow perfect alignment
    return !(rect1.x2 + COLLISION_TOLERANCE <= rect2.x1 || 
             rect1.x1 >= rect2.x2 + COLLISION_TOLERANCE || 
             rect1.y2 + COLLISION_TOLERANCE <= rect2.y1 || 
             rect1.y1 >= rect2.y2 + COLLISION_TOLERANCE);
};

// Enhanced guide generation
export const getSnapGuides = (position, elements, zoom, panOffset, excludeId = null, elementWidth = 0, elementHeight = 0) => {
    // const worldX = (position.x - panOffset.x) / zoom;
    // const worldY = (position.y - panOffset.y) / zoom;

    const worldX = position.x;
    const worldY = position.y;

    const rightEdge = worldX + elementWidth;
    const bottomEdge = worldY + elementHeight;
    
    let guides = [];
    
    elements.forEach(element => {
        if (element.id === excludeId) return;

        // Check vertical alignments
        const verticalCheckPoints = [
            { pos: element.x1, edge: worldX }, // Left to left
            { pos: element.x2, edge: worldX }, // Right to left
            { pos: element.x1, edge: rightEdge }, // Left to right
            { pos: element.x2, edge: rightEdge }, // Right to right
            { pos: element.x1 + (element.x2 - element.x1) / 2, edge: worldX + elementWidth / 2 } // Center to center
        ];

        verticalCheckPoints.forEach(({pos, edge}) => {
            if (Math.abs(edge - pos) * zoom < SNAP_THRESHOLD) {
                guides.push({
                    type: 'vertical',
                    position: pos,
                    start: Math.min(element.y1, worldY) - GRID_SIZE,
                    end: Math.max(element.y2, worldY + elementHeight) + GRID_SIZE
                });
            }
        });

        // Check horizontal alignments
        const horizontalCheckPoints = [
            { pos: element.y1, edge: worldY }, // Top to top
            { pos: element.y2, edge: worldY }, // Bottom to top
            { pos: element.y1, edge: bottomEdge }, // Top to bottom
            { pos: element.y2, edge: bottomEdge }, // Bottom to bottom
            { pos: element.y1 + (element.y2 - element.y1) / 2, edge: worldY + elementHeight / 2 } // Center to center
        ];

        horizontalCheckPoints.forEach(({pos, edge}) => {
            if (Math.abs(edge - pos) * zoom < SNAP_THRESHOLD) {
                guides.push({
                    type: 'horizontal',
                    position: pos,
                    start: Math.min(element.x1, worldX) - GRID_SIZE,
                    end: Math.max(element.x2, worldX + elementWidth) + GRID_SIZE
                });
            }
        });
    });

    return guides;
};

